// Outback Station — stage prop pack + placements (owner: the outback stage; see layout.js for the folder contract).
//
// register(D, H): registers all 'outback_*' prop builders on PropKit's definition table D.
// PLACEMENTS: set dressing for the Outback Station stage (mirrored (x,z) -> (-x,-z) with rotY + PI unless mirror: false).

const P = Math.PI;

export function register(D, H) {
  const { THREE, col, shade, mixc, puffGeo, blobGeo, PI, TAU, HP } = H;
  const NS = (m) => (H.noShadow ? H.noShadow(m) : m);

  // ------------------------------------------------------------------------------------------ Outback Palette
  const K = {
    ochre: '#c85a32', ochreDk: '#8f391b', ochreLt: '#df7b4d', sandstone: '#d47246', sandLt: '#e69868',
    ironstone: '#523024', clay: '#b8522c',
    wood: '#8c5d3c', woodDk: '#593922', woodLt: '#b5845b', jarrah: '#753f28', bleachWood: '#aba090',
    galv: '#b8bec2', galvDk: '#7d848a', galvLt: '#d5dade', zinc: '#9fa6aa',
    rust: '#8c3b20', rustLt: '#b35330', iron: '#32302e', ironLt: '#524f4b',
    cream: '#f0e4cc', weatherboard: '#e8dac0', corrugated: '#cfc5b0',
    corroRed: '#9e3828', corroGrn: '#3b5e45',
    uteBlue: '#3b6e8c', uteBlueDk: '#274c63', uteTray: '#7d5638', canvas: '#c7b897',
    baleJute: '#cbb58b', baleDk: '#a38d65', hay: '#d4a84e', hayDk: '#ad8332',
    gumBark: '#e6ded0', gumPatch: '#c99b78', gumBase: '#6e4c36',
    euca: '#5b7853', eucaDk: '#3f573a', eucaLt: '#7d996e',
    spinifex: '#8c8f4a', spinifexLt: '#b0ad5f', saltbush: '#869982', saltbushDk: '#627560',
    blackboy: '#26211e', signYel: '#ebb424', signBlk: '#1f1d1a',
    water: '#3a6b7c', brass: '#c49a45', lamp: '#ffd89a', glass: '#33444f',
  };

  const TPL = new Map();
  const tpl = (key, fn) => { let g = TPL.get(key); if (!g) { g = fn(); TPL.set(key, g); } return g; };
  const kf = (a) => (typeof a === 'number' ? a.toFixed(4) : String(a));
  const hash = (n) => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); };
  const pboxGeo = () => tpl('ob_pbox', () => new THREE.BoxGeometry(1, 1, 1));
  const pbox = (B, mat, c, w, h, d, x, y, z, o = {}) => B.add(mat, pboxGeo(), c, x, y, z, { ...o, sx: w, sy: h, sz: d });
  const cylGeo = (rt, rb, h, seg = 10, open = false) => tpl(['ob_cy', rt, rb, h, seg, open ? 1 : 0].map(kf).join('|'), () => new THREE.CylinderGeometry(rt, rb, h, seg, 1, open));
  const puff = (det, seed) => tpl('ob_pf|' + det + '|' + seed, () => puffGeo(det, seed));
  const blob = (r, det, seed) => tpl(['ob_bl', r, det, seed].map(kf).join('|'), () => blobGeo(r, det, seed));

  const ROOF = { roof: true };
  const RAIL = { rail: true };
  const colBox = (B, x, y, z, w, h, d, flag = undefined) => B.col(x - w / 2, y, z - d / 2, x + w / 2, y + h, z + d / 2, flag);

  // Strut between two 3D points (x0,y0,z0) -> (x1,y1,z1)
  function strut(B, mat, c, x0, y0, z0, x1, y1, z1, r = 0.04, o = {}) {
    const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
    const len = Math.hypot(dx, dy, dz);
    if (len < 1e-4) return;
    const mx = (x0 + x1) * 0.5, my = (y0 + y1) * 0.5, mz = (z0 + z1) * 0.5;
    // Use box strut aligned via rotation or stepped segments; for Y-dominant or axis-aligned use pbox/cyl
    const horiz = Math.hypot(dx, dz);
    if (horiz < 1e-4) {
      pbox(B, mat, c, r * 2, len, r * 2, mx, my, mz, o);
      return;
    }
    const ry = Math.atan2(dx, dz);
    const rx = Math.atan2(horiz, dy);
    B.add(mat, pboxGeo(), c, mx, my, mz, { ...o, sx: r * 2, sy: len, sz: r * 2, rx, ry });
  }

  // Ellipsoid foliage cluster helper
  function foliageCluster(B, cx, cy, cz, rx, ry, rz, count, rBase, cDk, cLt, seed) {
    const det = B.k?.qf >= 1 ? 2 : 1;
    for (let i = 0; i < count; i++) {
      const a = hash(seed + i * 3.1) * TAU;
      const u = hash(seed + i * 5.7) * 2 - 1;
      const rr = Math.pow(hash(seed + i * 7.3), 0.45);
      const sq = Math.sqrt(Math.max(0, 1 - u * u));
      const px = cx + Math.cos(a) * sq * rr * rx;
      const py = cy + u * rr * ry;
      const pz = cz + Math.sin(a) * sq * rr * rz;
      const sc = rBase * (0.72 + 0.45 * hash(seed + i * 11.1));
      const t = Math.max(0, Math.min(1, (u + 1) * 0.5 + 0.15 * (hash(seed + i * 13.7) - 0.5)));
      B.add('foliage', puff(det, (seed + i * 17) & 63), mixc(cDk, cLt, t), px, py, pz, {
        sx: sc * 1.15,
        sy: sc * 0.72,
        sz: sc * 1.1,
        ry: hash(seed + i * 19.3) * TAU,
      });
    }
  }

  const cx3 = (c) => { const k = col(c); return [k.r, k.g, k.b]; };
  const paintGeo = (g, c) => {
    const k = cx3(c), n = g.attributes.position.count, a = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) a.set(k, i * 3);
    g.setAttribute('color', new THREE.BufferAttribute(a, 3));
    return g;
  };
  const xform = (g, x, y, z, rx = 0, ry = 0, rz = 0, sx = 1, sy = sx, sz = sx) =>
    g.applyMatrix4(new THREE.Matrix4().compose(
      new THREE.Vector3(x, y, z),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz, 'YXZ')),
      new THREE.Vector3(sx, sy, sz)
    ));
  function mergeGeos(list) {
    let nv = 0, ni = 0;
    for (const g of list) { nv += g.attributes.position.count; ni += g.index ? g.index.count : g.attributes.position.count; }
    const pos = new Float32Array(nv * 3), nor = new Float32Array(nv * 3), uv = new Float32Array(nv * 2), cl = new Float32Array(nv * 3), idx = new Uint32Array(ni);
    let vo = 0, io = 0;
    for (const g of list) {
      const n = g.attributes.position.count;
      pos.set(g.attributes.position.array, vo * 3); nor.set(g.attributes.normal.array, vo * 3);
      if (g.attributes.uv) uv.set(g.attributes.uv.array, vo * 2);
      if (g.attributes.color) cl.set(g.attributes.color.array, vo * 3); else cl.fill(1, vo * 3, (vo + n) * 3);
      if (g.index) { const I = g.index.array; for (let k = 0; k < I.length; k++) idx[io++] = I[k] + vo; } else for (let k = 0; k < n; k++) idx[io++] = vo + k;
      vo += n;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3)); g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
    g.setAttribute('uv', new THREE.BufferAttribute(uv, 2)); g.setAttribute('color', new THREE.BufferAttribute(cl, 3));
    g.setIndex(new THREE.BufferAttribute(idx, 1));
    return g;
  }

  // ==========================================================================================================
  // 1. OUTBACK_WINDMILL — Iconic Southern Cross Galvanised Steel Lattice Bore Windmill (centre landmark)
  // ==========================================================================================================
  // Disc in the XZ plane, spun about local Y; B.spin(..., { rx: HP }) tilts local Y to the +Z wind axis.
  H.spinTemplate('outback_mill_wheel', () => {
    const Rw = 1.75, Ri = 0.42, blades = 18;
    const parts = [];
    for (let i = 0; i < blades; i++) {
      const a0 = (i / blades) * TAU, a1 = ((i + 1) / blades) * TAU, am = (a0 + a1) * 0.5;
      const ca = Math.cos(am), sa = Math.sin(am);
      parts.push(paintGeo(xform(new THREE.BoxGeometry(0.62, 0.06, 0.045), ca * Rw, 0, sa * Rw, 0, -(am + HP), 0), K.galv));
      parts.push(paintGeo(xform(new THREE.BoxGeometry(0.18, 0.05, 0.035), ca * Ri, 0, sa * Ri, 0, -(am + HP), 0), K.galvDk));
      const rb = (Rw + Ri) * 0.52;
      const bladeCol = i % 3 === 0 ? K.galvLt : K.galv;
      parts.push(paintGeo(xform(new THREE.BoxGeometry(0.34, 0.022, Rw - Ri - 0.08), ca * rb, 0.02, sa * rb, 0.32, -(am + HP), 0), bladeCol));
      if (i % 3 === 0) {
        parts.push(paintGeo(xform(new THREE.BoxGeometry(0.035, 0.035, Rw), ca * (Rw * 0.5), -0.02, sa * (Rw * 0.5), 0, -am, 0), K.ironLt));
      }
    }
    parts.push(paintGeo(xform(new THREE.CylinderGeometry(0.22, 0.26, 0.36, 12), 0, 0.06, 0), K.corroRed));
    parts.push(paintGeo(xform(new THREE.CylinderGeometry(0.08, 0.12, 0.24, 10), 0, 0.28, 0), K.iron));
    return mergeGeos(parts);
  });

  D.outback_windmill = {
    build(B, o) {
      const H0 = o.h || 8.8;
      const wBase = 1.05, wTop = 0.28;
      const legAt = (t) => wBase + (wTop - wBase) * t;

      // Concrete footing plinths + 4 battered galvanised steel angle-iron legs
      const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
      for (const [sx, sz] of corners) {
        pbox(B, 'paint', K.ochreDk, 0.36, 0.28, 0.36, sx * wBase, 0.14, sz * wBase);
        strut(B, 'metal', K.galvDk, sx * wBase, 0.2, sz * wBase, sx * wTop, H0, sz * wTop, 0.045);
      }

      // Horizontal girts & X-bracing across 5 tower bays
      const bays = [0.12, 0.32, 0.52, 0.72, 0.90];
      for (let k = 0; k < bays.length; k++) {
        const t = bays[k], y = t * H0, w = legAt(t);
        pbox(B, 'metal', K.galv, w * 2.04, 0.05, 0.05, 0, y, -w);
        pbox(B, 'metal', K.galv, w * 2.04, 0.05, 0.05, 0, y, w);
        pbox(B, 'metal', K.galv, 0.05, 0.05, w * 2.04, -w, y, 0);
        pbox(B, 'metal', K.galv, 0.05, 0.05, w * 2.04, w, y, 0);
        if (k < bays.length - 1) {
          const t1 = bays[k + 1], y1 = t1 * H0, w1 = legAt(t1);
          for (const s of [-1, 1]) {
            strut(B, 'metal', K.zinc, -w, y, s * w, w1, y1, s * w1, 0.022);
            strut(B, 'metal', K.zinc, w, y, s * w, -w1, y1, s * w1, 0.022);
            strut(B, 'metal', K.zinc, s * w, y, -w, s * w1, y1, w1, 0.022);
            strut(B, 'metal', K.zinc, s * w, y, w, s * w1, y1, -w1, 0.022);
          }
        }
      }

      // Timber maintenance platform at 72% height (y ~ 6.3 m)
      const yp = H0 * 0.72, wp = legAt(0.72) + 0.38;
      for (let i = -3; i <= 3; i++) {
        pbox(B, 'wood', i % 2 ? K.wood : K.woodLt, wp * 2.1, 0.06, 0.16, 0, yp, i * 0.18);
      }
      // Platform safety handrail
      for (const s of [-1, 1]) {
        pbox(B, 'metal', K.galvDk, wp * 2.1, 0.04, 0.04, 0, yp + 0.85, s * wp);
        pbox(B, 'metal', K.galvDk, 0.04, 0.04, wp * 2.1, s * wp, yp + 0.85, 0);
        for (const c2 of [-1, 1]) pbox(B, 'metal', K.galvDk, 0.04, 0.85, 0.04, s * wp, yp + 0.42, c2 * wp);
      }

      // Steel access ladder up the -Z face
      for (let y = 0.4; y < yp; y += 0.36) {
        const t = y / H0, wz = legAt(t) + 0.05;
        pbox(B, NS('metal'), K.ironLt, 0.42, 0.025, 0.025, 0, y, -wz);
      }

      // Central vertical pump rod & cast-iron borehead at the base
      B.add('metal', cylGeo(0.24, 0.30, 0.85, 12), K.corroRed, 0, 0.42, 0);
      B.add('metal', cylGeo(0.32, 0.32, 0.12, 12), K.iron, 0, 0.86, 0);
      B.add('metal', cylGeo(0.045, 0.045, H0, 8), K.galvDk, 0, H0 * 0.5, 0);
      // Discharge pipe leading to a corrugated bore header tank & cattle trough beside the tower
      pbox(B, 'metal', K.galvDk, 0.09, 0.09, 1.45, 0.65, 0.65, 0);
      B.add('metal', cylGeo(0.68, 0.68, 1.45, 14), K.galv, 1.42, 0.72, 0);
      for (let h = 0.2; h <= 1.3; h += 0.22) {
        B.add(NS('metal'), cylGeo(0.70, 0.70, 0.04, 14), K.galvDk, 1.42, h, 0);
      }
      B.add('metal', cylGeo(0.64, 0.64, 0.06, 12), K.water, 1.42, 1.42, 0);

      // Turntable gearbox & tail vane at the top (y = H0)
      B.add('metal', cylGeo(0.26, 0.30, 0.55, 12), K.iron, 0, H0 + 0.25, 0);
      pbox(B, 'metal', K.corroRed, 0.36, 0.38, 0.85, 0, H0 + 0.52, 0.15);
      // Tail boom & broad corrugated sheet tail fin (-Z side)
      strut(B, 'metal', K.galvDk, 0, H0 + 0.55, -0.2, 0, H0 + 0.75, -2.35, 0.04);
      strut(B, 'metal', K.galvDk, 0, H0 + 0.35, -0.2, 0, H0 + 0.45, -2.15, 0.03);
      pbox(B, 'metal', K.galvLt, 0.04, 1.15, 1.45, 0, H0 + 0.68, -2.55);
      pbox(B, 'paint', K.corroRed, 0.05, 0.18, 1.38, 0, H0 + 1.12, -2.55);
      pbox(B, 'paint', K.corroRed, 0.05, 0.18, 1.38, 0, H0 + 0.24, -2.55);

      // Spinning multi-blade wind wheel on the +Z nose
      B.spin('outback_mill_wheel', 0, H0 + 0.52, 0.68, { rx: HP, speed: 1.35 });

      // Solid collider around the tower base & bore tank so players have clean cover without snagging inside struts
      colBox(B, 0, 0, 0, 2.2, 4.5, 2.2, ROOF);
      colBox(B, 1.42, 0, 0, 1.4, 1.5, 1.4, ROOF);
    },
  };

  // ==========================================================================================================
  // 2. OUTBACK_HOMESTEAD — Homestead Veranda Bullnose Awning, Colonial Facade, Chimney & Rainwater Tank
  // ==========================================================================================================
  D.outback_homestead = {
    build(B) {
      // Local origin sits at (0, 0, -45.4) facing +Z (toward the arena centre)
      const W = 18.0, yDeck = 2.6, yEave = 5.35, yRidge = 7.15;

      // Turned jarrah veranda posts along the front of the veranda (z = +6.8 from backwall -> world z = -38.6)
      const postXs = [-8.5, -3.4, 3.4, 8.5];
      for (const px of postXs) {
        pbox(B, 'wood', K.jarrah, 0.22, 2.75, 0.22, px, yDeck + 1.375, 6.8);
        pbox(B, 'wood', K.woodDk, 0.30, 0.18, 0.30, px, yDeck + 0.09, 6.8);
        // Decorative timber fretwork brackets at top of each post
        pbox(B, 'wood', K.cream, 0.55, 0.26, 0.08, px, yEave - 0.22, 6.8);
      }
      // Side veranda posts
      for (const sx of [-1, 1]) {
        pbox(B, 'wood', K.jarrah, 0.22, 2.75, 0.22, sx * 8.5, yDeck + 1.375, 3.4);
        // Timber post-and-rail balustrade on outer side edges of the veranda
        pbox(B, 'wood', K.woodLt, 0.12, 0.10, 6.6, sx * 8.85, yDeck + 0.92, 3.5);
        pbox(B, 'wood', K.wood, 0.10, 0.08, 6.6, sx * 8.85, yDeck + 0.48, 3.5);
      }

      // Veranda front fascia beam & bullnose corrugated iron awning roof (z = 0..7.1)
      pbox(B, 'wood', K.jarrah, W + 0.6, 0.26, 0.24, 0, yEave - 0.05, 6.85);
      // Main sloped veranda roof
      pbox(B, 'metal', K.corroRed, W + 0.8, 0.14, 6.2, 0, yEave + 0.36, 3.1, { rx: 0.12 });
      // Curved bullnose lip at the front edge
      pbox(B, 'metal', K.corroRed, W + 0.8, 0.12, 0.95, 0, yEave + 0.02, 6.45, { rx: 0.42 });
      pbox(B, 'metal', K.galv, W + 0.9, 0.08, 0.10, 0, yEave - 0.18, 6.95);

      // Upper homestead pitched gable roof above the backwall (z = -3.2..0.4, y = 5.2..7.15)
      pbox(B, 'paint', K.weatherboard, W + 1.2, 1.4, 3.2, 0, 5.8, -1.6);
      pbox(B, 'metal', K.corroRed, W + 1.8, 0.22, 2.4, 0, yRidge - 0.35, -0.55, { rx: 0.38 });
      pbox(B, 'metal', K.corroRed, W + 1.8, 0.22, 2.4, 0, yRidge - 0.35, -2.65, { rx: -0.38 });
      pbox(B, 'metal', K.galvLt, W + 1.9, 0.16, 0.24, 0, yRidge + 0.1, -1.6);

      // Tall red-brick outback homestead chimney on the west side of the roof
      pbox(B, 'paint', K.ochreDk, 1.15, 3.4, 1.15, -5.8, 6.8, -1.4);
      pbox(B, 'paint', K.sandstone, 1.32, 0.22, 1.32, -5.8, 8.55, -1.4);
      B.add('paint', cylGeo(0.22, 0.26, 0.55, 10), K.clay, -5.8, 8.9, -1.4);

      // Homestead Fascia Signboard above the central veranda stair
      pbox(B, 'wood', K.jarrah, 6.4, 0.78, 0.14, 0, yEave + 0.48, 6.85);
      pbox(B, 'paint', K.cream, 6.1, 0.58, 0.16, 0, yEave + 0.48, 6.87);

      // Colonial timber door & sash windows along the homestead facade (z = 0.08)
      // Central doorway with screen door
      pbox(B, 'wood', K.jarrah, 1.9, 2.35, 0.16, 0, yDeck + 1.18, 0.08);
      pbox(B, 'wood', K.cream, 1.6, 2.15, 0.18, 0, yDeck + 1.12, 0.11);
      pbox(B, NS('gloss'), K.glass, 1.25, 0.85, 0.20, 0, yDeck + 1.62, 0.12);
      // Sash windows with green timber shutters + warm dusk-lit upper window blinds
      for (const wx of [-6.2, -3.1, 3.1, 6.2]) {
        pbox(B, 'wood', K.cream, 1.45, 1.55, 0.14, wx, yDeck + 1.35, 0.08);
        pbox(B, NS('gloss'), K.glass, 1.18, 1.28, 0.16, wx, yDeck + 1.35, 0.10);
        pbox(B, NS('glow'), K.lamp, 1.06, 0.48, 0.17, wx, yDeck + 1.65, 0.10, { glow: 1.15, ao: false });
        pbox(B, 'wood', K.corroGrn, 0.36, 1.52, 0.16, wx - 0.88, yDeck + 1.35, 0.10);
        pbox(B, 'wood', K.corroGrn, 0.36, 1.52, 0.16, wx + 0.88, yDeck + 1.35, 0.10);
      }
      // Warm veranda wall lanterns flanking the front door
      for (const lx of [-1.45, 1.45]) {
        pbox(B, 'metal', K.iron, 0.16, 0.28, 0.22, lx, yDeck + 1.95, 0.22);
        B.add(NS('glow'), puff(1, 3), K.lamp, lx, yDeck + 1.90, 0.28, { s: 0.12, glow: 1.8, ao: false });
      }

      // Corrugated rainwater tank on timber stand on the flanking veranda wing (x = 11.6, z = 2.6)
      pbox(B, 'wood', K.woodDk, 2.3, 0.85, 2.3, 11.6, yDeck + 0.42, 2.6);
      B.add('metal', cylGeo(1.1, 1.1, 2.1, 14), K.galv, 11.6, yDeck + 1.9, 2.6);
      for (let h = 1.0; h <= 2.8; h += 0.35) {
        B.add(NS('metal'), cylGeo(1.12, 1.12, 0.04, 14), K.galvDk, 11.6, yDeck + h, 2.6);
      }
      colBox(B, 11.6, yDeck, 2.6, 2.3, 3.0, 2.3, ROOF);
      // Off-limits roof collider over the veranda bullnose awning so nobody lands on the awning roof
      colBox(B, 0, yEave - 0.1, 3.4, W + 0.8, 2.4, 7.0, ROOF);
    },
  };

  // ==========================================================================================================
  // 3. OUTBACK_WOOLSHED — Shearing Shed Monitor Clerestory, Rotary Vents, Wool Hoist & Dock Details
  // ==========================================================================================================
  H.spinTemplate('outback_whirlybird', () => {
    const parts = [
      paintGeo(xform(new THREE.CylinderGeometry(0.26, 0.22, 0.30, 10), 0, 0.15, 0), K.galv),
      paintGeo(xform(new THREE.CylinderGeometry(0.32, 0.28, 0.12, 10), 0, 0.34, 0), K.galvLt),
    ];
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU;
      parts.push(paintGeo(xform(new THREE.BoxGeometry(0.03, 0.24, 0.08), Math.cos(a) * 0.27, 0.22, Math.sin(a) * 0.27, 0, -a + 0.3, 0), K.galvLt));
    }
    return mergeGeos(parts);
  });

  D.outback_woolshed = {
    build(B) {
      // Local origin at (-20.75, 0, -20.5): dresses the woolshed dock (y = 1.2) and bale loft (y = 2.6)
      // Outer west corrugated iron monitor roof house along x = -5.25..-2.65 (world x = -26.0..-23.4)
      const yLoft = 2.6, hClerestory = 2.4;
      pbox(B, 'metal', K.corrugated, 2.6, hClerestory, 10.6, -3.95, yLoft + hClerestory * 0.5, 0);
      // Sawtooth pitched corrugated roof cap
      pbox(B, 'metal', K.corroRed, 3.1, 0.18, 11.0, -3.95, yLoft + hClerestory + 0.22, 0, { rz: -0.18 });
      // Louvred timber vent windows along the east face of the monitor house
      for (const vz of [-3.4, 0, 3.4]) {
        pbox(B, 'wood', K.jarrah, 0.14, 1.1, 2.1, -2.62, yLoft + 1.35, vz);
        pbox(B, 'wood', K.woodDk, 0.18, 0.85, 1.85, -2.59, yLoft + 1.35, vz);
      }
      // Spinning whirlybird roof ventilators on the ridge
      B.spin('outback_whirlybird', -3.8, yLoft + hClerestory + 0.32, -3.2, { speed: 3.4 });
      B.spin('outback_whirlybird', -3.8, yLoft + hClerestory + 0.32, 3.2, { speed: 3.4 });

      // Overhead steel I-beam wool-bale gantry hoist projecting from the monitor roof over the loft & dock
      pbox(B, 'metal', K.corroRed, 4.6, 0.22, 0.22, -1.2, yLoft + 2.35, 3.8);
      strut(B, 'metal', K.iron, -2.6, yLoft + 1.55, 3.8, -0.2, yLoft + 2.35, 3.8, 0.045);
      // Hanging chain & bale hook at the tip of the hoist (above head height)
      B.add('metal', cylGeo(0.03, 0.03, 0.52, 6), K.iron, 0.85, yLoft + 2.02, 3.8);
      B.add('metal', cylGeo(0.14, 0.14, 0.12, 8), K.rust, 0.85, yLoft + 1.72, 3.8);

      // Solid roof collider on the monitor house so players use the 8x11 m bale loft deck cleanly
      colBox(B, -3.95, yLoft, 0, 2.65, hClerestory + 0.6, 10.8, ROOF);
    },
  };

  // ==========================================================================================================
  // 4. OUTBACK_UTE — Dusty Australian Station Flatbed Utility Truck (solid cover in the Station Yard)
  // ==========================================================================================================
  D.outback_ute = {
    build(B, o) {
      const bodyCol = o.color || K.uteBlue;
      // 4 chunky desert off-road tyres + steel rims
      for (const [wx, wz] of [[-0.88, 1.35], [0.88, 1.35], [-0.88, -1.25], [0.88, -1.25]]) {
        B.add('rubber', cylGeo(0.42, 0.42, 0.28, 12), K.iron, wx, 0.42, wz, { rz: HP });
        B.add('metal', cylGeo(0.24, 0.24, 0.30, 10), K.cream, wx, 0.42, wz, { rz: HP });
      }
      // Heavy chassis frame & side steps
      pbox(B, 'metal', K.iron, 1.65, 0.24, 4.2, 0, 0.48, 0);
      // Front bonnet / hood & fenders (+Z front)
      pbox(B, 'paint', bodyCol, 1.82, 0.68, 1.45, 0, 0.88, 1.42);
      pbox(B, 'paint', shade(bodyCol, 1.08), 1.56, 0.12, 1.35, 0, 1.24, 1.38);
      // Chrome/galv radiator grille & dual headlights
      pbox(B, 'metal', K.galv, 1.32, 0.38, 0.08, 0, 0.88, 2.15);
      for (const sx of [-0.58, 0.58]) {
        B.add('glow', cylGeo(0.13, 0.13, 0.08, 10), K.lamp, sx, 0.92, 2.16, { rx: HP, glow: 0.4 });
      }
      // Heavy steel Outback Roos / Bullbar on the front bumper + UHF aerial whip
      pbox(B, 'metal', K.galvLt, 1.94, 0.14, 0.18, 0, 0.58, 2.26);
      for (const sx of [-0.78, -0.32, 0.32, 0.78]) {
        pbox(B, 'metal', K.galv, 0.07, 0.68, 0.07, sx, 0.92, 2.28);
      }
      pbox(B, 'metal', K.galv, 1.72, 0.07, 0.07, 0, 1.22, 2.28);
      pbox(B, NS('metal'), K.iron, 0.025, 1.15, 0.025, -0.78, 1.8, 2.28);

      // Cab & windscreen (z = -0.15..0.75)
      pbox(B, 'paint', bodyCol, 1.80, 0.72, 1.05, 0, 0.88, 0.25);
      pbox(B, 'gloss', K.glass, 1.62, 0.56, 0.88, 0, 1.48, 0.22);
      pbox(B, 'paint', K.cream, 1.72, 0.12, 1.02, 0, 1.80, 0.18);
      // Roof-mounted spotlight bar & sun visor
      pbox(B, 'paint', shade(bodyCol, 0.88), 1.68, 0.08, 0.28, 0, 1.74, 0.76, { rx: 0.25 });
      pbox(B, 'metal', K.iron, 1.2, 0.10, 0.10, 0, 1.90, 0.35);

      // Timber & steel drop-side flatbed tray (z = -2.25..-0.30)
      pbox(B, 'wood', K.uteTray, 1.92, 0.14, 2.05, 0, 0.78, -1.28);
      pbox(B, 'wood', K.wood, 1.92, 0.38, 0.08, 0, 1.02, -2.28);
      for (const sx of [-0.92, 0.92]) {
        pbox(B, 'wood', K.wood, 0.08, 0.38, 2.02, sx, 1.02, -1.28);
        // Steel headache rack behind cab
        pbox(B, 'metal', K.iron, 0.06, 0.95, 0.06, sx, 1.32, -0.30);
      }
      pbox(B, 'metal', K.iron, 1.88, 0.06, 0.06, 0, 1.78, -0.30);

      // Cargo in tray: 44-gallon red fuel drum, rolled canvas swag, and spare tyre
      B.add('metal', cylGeo(0.29, 0.29, 0.86, 12), K.corroRed, -0.46, 1.28, -0.82);
      B.add('paint', cylGeo(0.26, 0.26, 0.92, 10), K.canvas, 0.42, 1.12, -1.15, { rz: HP, ry: 0.25 });
      B.add('rubber', cylGeo(0.38, 0.38, 0.24, 12), K.iron, 0.35, 0.98, -1.85);

      colBox(B, 0, 0, 0, 1.96, 1.85, 4.55, ROOF);
    },
  };

  // ==========================================================================================================
  // 5. OUTBACK_WATERTANK & OUTBACK_TROUGH — Corrugated Bore Tank on Stand & Stock Drinking Trough
  // ==========================================================================================================
  D.outback_watertank = {
    build(B, o) {
      const r = o.r || 1.35, hStand = o.hStand ?? 1.1, hTank = o.hTank || 1.95;
      // Heavy jarrah timber cribbing stand
      if (hStand > 0.05) {
        const w = r * 1.35;
        for (const [sx, sz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
          pbox(B, 'wood', K.woodDk, 0.22, hStand, 0.22, sx * w * 0.42, hStand * 0.5, sz * w * 0.42);
        }
        pbox(B, 'wood', K.wood, w, 0.16, w, 0, hStand - 0.08, 0);
      }
      // Corrugated zincalume tank body with ribs
      const y0 = hStand;
      B.add('metal', cylGeo(r, r, hTank, 16), K.galv, 0, y0 + hTank * 0.5, 0);
      for (let h = 0.22; h < hTank; h += 0.32) {
        B.add(NS('metal'), cylGeo(r + 0.02, r + 0.02, 0.045, 16), K.galvDk, 0, y0 + h, 0);
      }
      // Conical tank roof cap
      B.add('metal', cylGeo(0.12, r + 0.06, 0.32, 16), K.galvLt, 0, y0 + hTank + 0.16, 0);
      colBox(B, 0, 0, 0, r * 2.05, y0 + hTank + 0.3, r * 2.05, ROOF);
    },
  };

  D.outback_trough = {
    build(B, o) {
      const w = o.w || 3.2, d = o.d || 0.95, h = 0.72;
      // Concrete/stone sleepers under trough
      pbox(B, 'paint', K.ochreDk, 0.32, 0.18, d + 0.1, -w * 0.38, 0.09, 0);
      pbox(B, 'paint', K.ochreDk, 0.32, 0.18, d + 0.1, w * 0.38, 0.09, 0);
      // Galvanised trough body & rolled top rim
      pbox(B, 'metal', K.galv, w, h - 0.16, d, 0, 0.18 + (h - 0.16) * 0.5, 0);
      pbox(B, 'metal', K.galvDk, w + 0.08, 0.08, d + 0.08, 0, h, 0);
      // Cool bore water surface inside + float valve housing at one end
      pbox(B, 'paint', K.water, w - 0.14, 0.04, d - 0.14, 0, h - 0.04, 0);
      pbox(B, 'metal', K.corroRed, 0.48, 0.12, d + 0.06, w * 0.5 - 0.26, h + 0.05, 0);
      if (o.solid !== false) colBox(B, 0, 0, 0, w, h + 0.08, d);
    },
  };

  // ==========================================================================================================
  // 6. OUTBACK_STOCKFENCE — Gidgee Post-and-Rail Stockyard Fence (rail: true collider)
  // ==========================================================================================================
  D.outback_stockfence = {
    build(B, o) {
      const len = o.len || 4.8, h = o.h || 1.08;
      const bays = Math.max(1, Math.round(len / 2.4));
      const step = len / bays;
      for (let i = 0; i <= bays; i++) {
        const x = -len * 0.5 + i * step;
        const twist = (hash(i * 7.1 + len) - 0.5) * 0.15;
        B.add('wood', cylGeo(0.09, 0.11, h, 7), i % 2 ? K.woodDk : K.jarrah, x, h * 0.5, 0, { ry: twist });
      }
      // Three horizontal rough-sawn eucalyptus rails
      for (const ry of [0.32, 0.64, 0.96]) {
        pbox(B, 'wood', K.bleachWood, len + 0.12, 0.09, 0.06, 0, ry, 0.06);
      }
      if (o.solid !== false) colBox(B, 0, 0, 0, len + 0.18, h, 0.28, RAIL);
    },
  };

  // ==========================================================================================================
  // 7. OUTBACK_WOOLBALES & OUTBACK_HAYBALES — Jute Merino Wool Bales & Rolled Golden Hay Bales
  // ==========================================================================================================
  D.outback_woolbales = {
    build(B, o) {
      const stack = o.stack || 2;
      // Base row of 2 stencilled jute wool packs bound with steel bands
      const bales = [
        [-0.58, 0.55, 0, 0.04, K.baleJute],
        [0.58, 0.55, 0.05, -0.05, K.baleDk],
      ];
      if (stack >= 3) bales.push([0, 1.62, -0.02, 0.08, K.baleJute]);
      for (const [bx, by, bz, ry, c] of bales) {
        pbox(B, 'paint', c, 1.06, 1.08, 1.02, bx, by, bz, { ry });
        // Steel bale straps & red stencil patch
        pbox(B, NS('metal'), K.iron, 1.08, 0.04, 1.04, bx, by - 0.26, bz, { ry });
        pbox(B, NS('metal'), K.iron, 1.08, 0.04, 1.04, bx, by + 0.26, bz, { ry });
        pbox(B, NS('paint'), K.corroRed, 0.52, 0.22, 1.04, bx, by + 0.02, bz, { ry });
      }
      colBox(B, 0, 0, 0, 2.28, stack >= 3 ? 2.15 : 1.12, 1.12, stack >= 3 ? ROOF : undefined);
    },
  };

  D.outback_haybales = {
    build(B, o) {
      const n = o.n || 2;
      B.add('paint', cylGeo(0.68, 0.68, 1.25, 12), K.hay, -0.65, 0.68, 0, { rz: HP, ry: 0.12 });
      B.add(NS('paint'), cylGeo(0.70, 0.70, 0.05, 12), K.rope || K.baleDk, -0.65, 0.68, 0, { rz: HP, ry: 0.12 });
      if (n >= 2) {
        B.add('paint', cylGeo(0.68, 0.68, 1.25, 12), K.hayDk, 0.68, 0.68, 0.12, { rz: HP, ry: -0.18 });
      }
      if (n >= 3) {
        B.add('paint', cylGeo(0.64, 0.64, 1.20, 12), K.hay, 0.02, 1.82, 0.05, { rz: HP, ry: 0.05 });
      }
      colBox(B, 0, 0, 0, n >= 2 ? 2.6 : 1.4, n >= 3 ? 2.4 : 1.35, 1.4, ROOF);
    },
  };

  // ==========================================================================================================
  // 8. OUTBACK_GHOSTGUM — Sculpted Australian Ghost Gum / River Red Gum (Eucalyptus)
  // ==========================================================================================================
  D.outback_ghostgum = {
    build(B, o) {
      const s = o.s || 1.0, seed = o.seed || 1;
      const hTrunk = 3.2 * s;
      // Rough dark basal bark stocking + smooth cream/ochre upper ghost-gum trunk
      B.add('wood', cylGeo(0.36 * s, 0.52 * s, 1.1 * s, 9), K.gumBase, 0, 0.55 * s, 0);
      B.add('paint', cylGeo(0.26 * s, 0.37 * s, hTrunk - 0.9 * s, 9), K.gumBark, 0, 0.9 * s + (hTrunk - 0.9 * s) * 0.5, 0, {
        rx: 0.06,
        rz: -0.04,
      });

      // 3 gnarled spreading cream limbs reaching into discrete sage-olive eucalyptus canopy clouds
      const limbs = [
        [1.35 * s, (hTrunk + 1.45 * s), 0.65 * s, 1.65 * s, 0.95 * s, 1.55 * s],
        [-1.25 * s, (hTrunk + 1.25 * s), -0.75 * s, 1.55 * s, 0.90 * s, 1.45 * s],
        [0.15 * s, (hTrunk + 2.05 * s), -0.25 * s, 1.75 * s, 1.05 * s, 1.65 * s],
      ];
      limbs.forEach(([lx, ly, lz, rx, ry, rz], idx) => {
        strut(B, 'paint', idx === 1 ? K.gumPatch : K.gumBark, 0, hTrunk - 0.3 * s, 0, lx * 0.78, ly - 0.3 * s, lz * 0.78, 0.14 * s);
        foliageCluster(B, lx, ly, lz, rx, ry, rz, 10, 0.62 * s, K.eucaDk, K.eucaLt, seed * 31 + idx * 19);
      });

      if (o.solid !== false) colBox(B, 0, 0, 0, 0.85 * s, 3.2 * s, 0.85 * s, ROOF);
    },
  };

  // ==========================================================================================================
  // 9. OUTBACK_GRASSTREE, OUTBACK_SPINIFEX & OUTBACK_SALTBUSH — Authentic Flinders Ranges Desert Flora
  // ==========================================================================================================
  D.outback_grasstree = {
    build(B, o) {
      const s = o.s || 1.0, h = (o.h || 1.35) * s;
      // Fire-blackened Xanthorrhoea caudex trunk
      B.add('wood', cylGeo(0.18 * s, 0.24 * s, h, 8), K.blackboy, 0, h * 0.5, 0, { rx: 0.05 });
      // Dense spherical skirt/crown of spiky grass needles
      foliageCluster(B, 0, h + 0.18 * s, 0, 0.68 * s, 0.48 * s, 0.68 * s, 9, 0.34 * s, K.eucaDk, K.spinifexLt, (o.seed || 7) * 13);
      // Tall kangaroo-tail flowering spike rising from the crown
      B.add('wood', cylGeo(0.03 * s, 0.045 * s, 1.45 * s, 6), K.woodDk, 0, h + 0.95 * s, 0);
      B.add('paint', cylGeo(0.055 * s, 0.055 * s, 0.68 * s, 7), K.cream, 0, h + 1.45 * s, 0);
      if (o.solid !== false) colBox(B, 0, 0, 0, 0.52 * s, h + 0.45 * s, 0.52 * s, ROOF);
    },
  };

  D.outback_spinifex = {
    build(B, o) {
      const s = o.s || 1.0, seed = o.seed || 3;
      foliageCluster(B, 0, 0.22 * s, 0, 0.75 * s, 0.32 * s, 0.75 * s, 7, 0.28 * s, K.spinifex, K.spinifexLt, seed * 23);
    },
  };

  D.outback_saltbush = {
    build(B, o) {
      const s = o.s || 1.0, seed = o.seed || 5;
      foliageCluster(B, 0, 0.34 * s, 0, 0.85 * s, 0.45 * s, 0.82 * s, 8, 0.34 * s, K.saltbushDk, K.saltbush, seed * 29);
    },
  };

  // ==========================================================================================================
  // 10. OUTBACK_ROCKTOR — Layered Red-Ochre Flinders Sandstone Outcrop & Boulders
  // ==========================================================================================================
  D.outback_rocktor = {
    build(B, o) {
      const s = o.s || 1.0, seed = o.seed || 11;
      const det = B.k?.qf >= 1 ? 2 : 1;
      // Stratified horizontal sandstone slabs + weathered blob boulders
      B.add('paint', blob(0.95 * s, det, seed), K.sandstone, 0, 0.42 * s, 0, { sx: 1.35, sy: 0.62, sz: 1.1 });
      B.add('paint', blob(0.78 * s, det, seed + 3), K.ochre, 0.18 * s, 0.82 * s, -0.08 * s, { sx: 1.15, sy: 0.55, sz: 0.95, ry: 0.8 });
      B.add('paint', blob(0.52 * s, det, seed + 7), K.sandLt, -0.45 * s, 0.32 * s, 0.38 * s, { sx: 1.2, sy: 0.5, sz: 1.0, ry: 2.1 });
      if (o.solid !== false) colBox(B, 0, 0, 0, 1.8 * s, 1.15 * s, 1.5 * s, ROOF);
    },
  };

  // ==========================================================================================================
  // 11. OUTBACK_ROADSIGN & OUTBACK_LAMP — Aussie Kangaroo Diamond Signpost & Gooseneck Yard Lamp
  // ==========================================================================================================
  D.outback_roadsign = {
    build(B, o = {}) {
      B.add('metal', cylGeo(0.04, 0.045, 2.75, 8), K.galvDk, 0, 1.375, 0);
      // Yellow diamond kangaroo crossing sign (rotated 45 deg around Z)
      pbox(B, 'paint', K.signYel, 0.68, 0.68, 0.04, 0, 2.25, 0.05, { rz: PI * 0.25 });
      pbox(B, 'paint', K.signBlk, 0.56, 0.56, 0.045, 0, 2.25, 0.052, { rz: PI * 0.25 });
      pbox(B, 'paint', K.signYel, 0.50, 0.50, 0.05, 0, 2.25, 0.055, { rz: PI * 0.25 });
      // Distance fingerpost boards above
      pbox(B, 'paint', K.cream, 1.05, 0.18, 0.04, 0.32, 2.78, 0, { ry: 0.2 });
      pbox(B, 'paint', K.cream, 0.95, 0.18, 0.04, -0.28, 2.98, 0, { ry: -0.35 });
      if (o.solid !== false) colBox(B, 0, 0, 0, 0.24, 2.8, 0.24, ROOF);
    },
  };

  D.outback_lamp = {
    build(B, o = {}) {
      // Tall jarrah station post + galvanised gooseneck arm reaching 0.92 m toward arena centre (matching setLevelLamps)
      B.add('wood', cylGeo(0.09, 0.13, 4.85, 8), K.jarrah, 0, 2.425, 0);
      B.add('metal', cylGeo(0.14, 0.16, 0.35, 8), K.iron, 0, 0.175, 0);
      // Horizontal arm toward +X in local frame (orient via rotY so +X points toward X=0)
      pbox(B, 'metal', K.galvDk, 0.96, 0.05, 0.05, 0.46, 4.98, 0);
      strut(B, 'metal', K.galvDk, 0.06, 4.55, 0, 0.62, 4.98, 0, 0.03);
      // Enamel shade cone + glowing dusk bulb at (0.92, 4.9, 0)
      B.add('metal', cylGeo(0.08, 0.28, 0.16, 10), K.corroGrn, 0.92, 4.92, 0);
      B.add(NS('glow'), puff(1, 5), K.lamp, 0.92, 4.82, 0, { s: 0.14, glow: 1.8, ao: false });
      if (o.solid !== false) colBox(B, 0, 0, 0, 0.32, 4.9, 0.32, ROOF);
    },
  };
}

// ==========================================================================================================
// PLACEMENTS — Set dressing for Outback Station (Alpha half; mirrored automatically for Bravo unless mirror: false)
// ==========================================================================================================
export const PLACEMENTS = [
  // Central Southern Cross Bore Windmill on the central octagonal platform (single, not mirrored)
  { type: 'outback_windmill', pos: [0, 0.42, 0], rotY: 0, mirror: false },

  // Homestead Veranda architectural set piece (at each team's spawn)
  { type: 'outback_homestead', pos: [0, 0, -45.4], rotY: 0 },

  // Shearing Shed upper clerestory, whirlybird vents & wool-bale hoist
  { type: 'outback_woolshed', pos: [-20.75, 0, -20.5], rotY: 0 },

  // Station Yard Flatbed Ute (parked on the east side of the yard as solid cover)
  { type: 'outback_ute', pos: [5.4, 0, -18.5], rotY: -0.35 },

  // Corrugated Bore Water Tank & Stock Drinking Troughs
  { type: 'outback_watertank', pos: [-6.8, 0, -24.2], rotY: 0.4, r: 1.35, hStand: 1.0, hTank: 1.9 },
  { type: 'outback_trough', pos: [-6.8, 0, -21.4], rotY: 0, w: 2.6, d: 0.85 },
  { type: 'outback_trough', pos: [-14.2, 0, -30.8], rotY: 0.2, w: 2.8, d: 0.85 },

  // Gidgee Post-and-Rail Stockyard Fences (rail: true — kids blocked/can hop on top, shots & squids pass through)
  { type: 'outback_stockfence', pos: [-10.2, 0, -30.5], rotY: 0, len: 4.6 },
  { type: 'outback_stockfence', pos: [-12.5, 0, -11.2], rotY: P * 0.5, len: 3.6 },
  { type: 'outback_stockfence', pos: [10.2, 0, -31.8], rotY: -0.25, len: 4.2 },

  // Stencilled Jute Merino Wool Bales & Rolled Golden Hay Bales (cover on the loading dock, loft & yard)
  { type: 'outback_woolbales', pos: [-14.2, 1.2, -24.6], rotY: P * 0.5, stack: 2 },
  { type: 'outback_woolbales', pos: [-20.2, 2.6, -19.8], rotY: 0.12, stack: 2, notIn: 'zones' },
  { type: 'outback_haybales', pos: [-4.8, 0, -31.6], rotY: 0.35, n: 2 },
  { type: 'outback_haybales', pos: [-18.2, 0, -32.2], rotY: -0.4, n: 3 },

  // Sculpted Australian Ghost Gums / River Red Gums along the dry creek banks & station perimeter
  { type: 'outback_ghostgum', pos: [-23.8, -0.7, -5.2], rotY: 0.6, s: 1.12, seed: 11 },
  { type: 'outback_ghostgum', pos: [23.6, -0.7, -4.8], rotY: 2.1, s: 1.05, seed: 23 },
  { type: 'outback_ghostgum', pos: [-24.4, 0, -36.5], rotY: 1.4, s: 1.08, seed: 37 },
  { type: 'outback_ghostgum', pos: [24.2, 1.1, -33.8], rotY: -0.9, s: 0.98, seed: 51 },

  // Iconic Grass Trees (Xanthorrhoea), Spiky Spinifex & Old Man Saltbush across the Red Sand Hills & Creek Banks
  { type: 'outback_grasstree', pos: [23.8, 2.2, -25.4], rotY: 0.4, s: 1.1, seed: 3 },
  { type: 'outback_grasstree', pos: [24.4, 3.1, -14.2], rotY: 1.8, s: 1.0, seed: 9 },
  { type: 'outback_grasstree', pos: [14.8, 1.1, -33.8], rotY: -0.7, s: 0.92, seed: 15 },
  { type: 'outback_spinifex', pos: [14.2, 1.1, -19.5], rotY: 0.2, s: 1.15, seed: 5 },
  { type: 'outback_spinifex', pos: [23.2, 2.2, -12.2], rotY: 1.5, s: 1.1, seed: 12 },
  { type: 'outback_spinifex', pos: [17.8, 1.1, -34.2], rotY: 2.4, s: 1.05, seed: 19 },
  { type: 'outback_saltbush', pos: [-7.2, -0.7, -6.8], rotY: 0.8, s: 1.1, seed: 7 },
  { type: 'outback_saltbush', pos: [7.0, -0.7, -6.5], rotY: -1.1, s: 1.05, seed: 17 },
  { type: 'outback_saltbush', pos: [-19.4, -0.7, -6.6], rotY: 0.3, s: 1.15, seed: 29 },
  { type: 'outback_saltbush', pos: [22.8, 1.1, -9.8], rotY: 1.9, s: 1.0, seed: 41 },

  // Layered Red-Ochre Sandstone Tors on the Red Sand Hills & Outer Rim
  { type: 'outback_rocktor', pos: [24.2, 2.2, -27.2], rotY: 0.5, s: 1.15, seed: 13 },
  { type: 'outback_rocktor', pos: [24.5, 3.1, -18.5], rotY: -0.8, s: 1.05, seed: 27 },

  // Outback Kangaroo Warning Signpost at the bridge approach
  { type: 'outback_roadsign', pos: [7.6, 0, -9.2], rotY: -0.25 },

  // Station Yard Gooseneck Lamps (matching LAYOUT.decor.lampPools so dusk light pools align with the bulbs)
  { type: 'outback_lamp', pos: [-11.8, 0, -33.0], rotY: 0 },
  { type: 'outback_lamp', pos: [11.8, 0, -33.0], rotY: P },
  { type: 'outback_lamp', pos: [-12.2, 0, -12.8], rotY: 0 },
  { type: 'outback_lamp', pos: [12.2, 0, -12.8], rotY: P },
];
